import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LogIn, LogOut, Menu, Moon, ShieldCheck, Sun, X, Zap } from 'lucide-react';
import { BrandMark } from '@/components/common/BrandMark';
import { MediaImage } from '@/components/common/MediaImage';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/hooks/useTheme';
import { usePublicContent } from '@/hooks/api/usePublic';
import { useAdminStats, useAuthLogout } from '@/hooks/api/usePortal';
import { ROUTES } from '@/constants';
import { cn } from '@/lib/utils';
import { adminNav, customerNav, isGroup, type NavEntry, type NavGroup, type NavItem } from '@/components/common/Sidebar';

interface NavbarProps {
  variant?: 'public' | 'app';
  appNav?: 'customer' | 'admin';
}

const PUBLIC_LINKS = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About', end: false },
  { to: '/plans', label: 'Plans', end: false },
  { to: '/gallery', label: 'Gallery', end: false },
  { to: '/contact', label: 'Contact', end: false },
];

export function Navbar({ variant = 'public', appNav }: NavbarProps) {
  const { resolvedTheme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data } = usePublicContent();
  const logoUrl = data?.logo?.fileUrl;
  const companyName = data?.settings?.['company.name'] ?? 'DreamPick Private Limited';
  const logout = useAuthLogout();
  const nav = useNavigate();
  const location = useLocation();
  const { data: statsData } = useAdminStats();
  const adminCounts = (statsData?.totals as Record<string, number> | undefined) ?? {};

  const handleLogout = async () => {
    await logout.mutateAsync();
    nav('/', { replace: true });
  };

  const appEntries: NavEntry[] = appNav === 'admin' ? adminNav : appNav === 'customer' ? customerNav : [];
  const currentPath = location.pathname;

  useEffect(() => {
    setMobileOpen(false);
  }, [currentPath]);

  return (
    <header
      className="sticky top-0 z-40 border-b border-gold/8 bg-background/60 backdrop-blur-xl supports-[backdrop-filter]:bg-background/40"
      data-testid="site-navbar"
    >
      <div className="container flex h-16 items-center justify-between">
        <Link to={ROUTES.home} aria-label={`${companyName} home`} className="flex items-center gap-3">
          {logoUrl ? (
            <MediaImage
              src={logoUrl}
              alt={companyName}
              ratio="auto"
              className="h-9 w-9 rounded-lg shadow-md shadow-gold/10"
              placeholder="Logo"
            />
          ) : (
            <BrandMark />
          )}
          <span className="hidden text-sm font-medium text-foreground/80 sm:inline">
            <span className="sr-only">Company: </span>
            {companyName}
          </span>
        </Link>

        {variant === 'public' && (
          <nav aria-label="Primary" className="hidden items-center gap-1 text-sm md:flex">
            {PUBLIC_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  cn(
                    'relative rounded-lg px-3 py-2 transition-all duration-200',
                    isActive
                      ? 'text-gold font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-white/5',
                  )
                }
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <span className="absolute bottom-0 left-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-gradient-to-r from-gold via-amber-300 to-gold shadow-sm shadow-gold/30" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label="Toggle theme"
            data-testid="theme-toggle-btn"
            className="rounded-lg hover:bg-white/5"
          >
            {resolvedTheme === 'dark' ? <Sun className="h-4 w-4 text-gold/70" /> : <Moon className="h-4 w-4" />}
          </Button>

          {variant === 'public' && (
            <>
              <Button
                asChild
                size="sm"
                className="hidden rounded-full bg-gradient-to-r from-gold to-amber-500 text-gold-foreground font-semibold shadow-lg shadow-gold/20 hover:shadow-xl hover:shadow-gold/30 hover:-translate-y-0.5 transition-all sm:inline-flex"
                data-testid="nav-login-btn"
              >
                <Link to={ROUTES.auth.login}>
                  <LogIn className="h-4 w-4" />
                  Login
                </Link>
              </Button>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="hidden rounded-full text-xs text-muted-foreground hover:text-gold md:inline-flex"
                data-testid="nav-admin-login-btn"
              >
                <Link to="/auth/admin-login">
                  <ShieldCheck className="h-4 w-4" />
                  Admin
                </Link>
              </Button>
            </>
          )}

          {variant === 'app' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              disabled={logout.isPending}
              className="rounded-full text-muted-foreground hover:text-destructive"
              data-testid="nav-logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            className="rounded-lg md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {mobileOpen && variant === 'public' && (
        <div
          id="mobile-nav"
          className="border-t border-gold/8 bg-background/95 backdrop-blur-xl md:hidden"
          data-testid="mobile-menu"
        >
          <nav aria-label="Mobile" className="container flex flex-col gap-1 py-3 text-sm">
            {PUBLIC_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'rounded-xl px-4 py-2.5 transition-all duration-200',
                    isActive
                      ? 'bg-gold/10 text-gold font-medium'
                      : 'text-muted-foreground hover:bg-white/5 hover:text-foreground',
                  )
                }
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to={ROUTES.auth.login}
              onClick={() => setMobileOpen(false)}
              className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-gold to-amber-500 px-4 py-2.5 text-center text-sm font-semibold text-gold-foreground shadow-lg shadow-gold/25"
              data-testid="mobile-login-btn"
            >
              <Zap className="h-4 w-4" />
              Login
            </Link>
          </nav>
        </div>
      )}

      {mobileOpen && variant === 'app' && appEntries.length > 0 && (
        <div
          id="mobile-nav"
          className="border-t border-gold/8 bg-background/95 backdrop-blur-xl lg:hidden"
          data-testid="mobile-menu"
        >
          <nav aria-label="App" className="flex max-h-[75vh] flex-col gap-0.5 overflow-y-auto p-3 text-sm">
            {appEntries.map((entry) =>
              isGroup(entry) ? (
                <MobileGroup key={entry.label} group={entry} counts={adminCounts} />
              ) : (
                <MobileLink key={entry.to} item={entry} counts={adminCounts} />
              ),
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

function MobileLink({ item, counts }: { item: NavItem; counts: Record<string, number> }) {
  const dotCount = item.dotKey ? counts[item.dotKey] ?? 0 : 0;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/dashboard' || item.to === '/admin'}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-xl px-4 py-2.5 transition-all duration-200',
          isActive
            ? 'bg-gold/10 text-gold font-medium'
            : 'text-muted-foreground hover:bg-white/5 hover:text-foreground',
        )
      }
      data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <item.icon className="h-4 w-4" />
      {item.label}
      {dotCount > 0 && (
        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 font-mono text-[10px] font-bold text-primary-foreground">
          {dotCount > 99 ? '99+' : dotCount}
        </span>
      )}
    </NavLink>
  );
}

function MobileGroup({ group, counts }: { group: NavGroup; counts: Record<string, number> }) {
  return (
    <div className="mt-1">
      <p className="flex items-center gap-2 px-4 pb-1 pt-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60">
        <group.icon className="h-3.5 w-3.5" />
        {group.label}
      </p>
      <div className="ml-3 space-y-0.5 border-l border-border/60 pl-2">
        {group.children.map((child) => (
          <MobileLink key={child.to} item={child} counts={counts} />
        ))}
      </div>
    </div>
  );
}
